// Procedural backdrops. Pixel scenes are drawn at a low resolution and scaled
// up by a whole number, so every pixel is the same size and the art stays
// crisp. Smooth scenes draw at screen resolution. Nothing here loads an image.

import type { ThemeId } from "../../shared/types";

export interface SceneFrame {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  t: number; // seconds since start
}

/** How a scene is drawn for where it shows: clearTop keeps the top of the sky free for words (the sign-in and the first question, Fixes 12). */
export interface SceneOpts {
  clearTop?: boolean;
}

export interface Scene {
  pixel: boolean;
  fps: number; // when motion is on; 0 = a still picture
  build(w: number, h: number, o?: SceneOpts): SceneState;
  frame(f: SceneFrame, s: SceneState): void;
}

type SceneState = Record<string, unknown>;

// ---------------------------------------------------------------------------
// helpers

export type RGB = [number, number, number];

export function hex(h: string): RGB {
  const n = parseInt(h.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export const css = (c: RGB, a = 1) => (a >= 1 ? `rgb(${c[0]},${c[1]},${c[2]})` : `rgba(${c[0]},${c[1]},${c[2]},${a})`);

export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function noise1(seed: number) {
  const r = rng(seed);
  const vals = Array.from({ length: 512 }, () => r());
  return (x: number) => {
    const i = Math.floor(x);
    const f = x - i;
    const a = vals[((i % 512) + 512) % 512];
    const b = vals[(((i + 1) % 512) + 512) % 512];
    const u = f * f * (3 - 2 * f);
    return a + (b - a) * u;
  };
}

export function fractal(seed: number, octaves = 4) {
  const ns = Array.from({ length: octaves }, (_, i) => noise1(seed + i * 101));
  return (x: number) => {
    let v = 0;
    let amp = 1;
    let freq = 1;
    let norm = 0;
    for (const n of ns) {
      v += n(x * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    return v / norm;
  };
}

export const BAYER = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((r) => r.map((v) => (v + 0.5) / 16));

export function layer(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  return [c, ctx];
}

/** A dithered vertical gradient, the way pixel artists band a sky. */
export function ditherSky(ctx: CanvasRenderingContext2D, w: number, y0: number, y1: number, stops: string[]) {
  const pal = stops.map(hex);
  const n = pal.length - 1;
  const img = ctx.createImageData(w, Math.max(1, y1 - y0));
  for (let y = y0; y < y1; y++) {
    const t = ((y - y0) / Math.max(1, y1 - y0 - 1)) * n;
    const i = Math.min(n - 1, Math.floor(t));
    const f = t - i;
    for (let x = 0; x < w; x++) {
      const c = f > BAYER[y & 3][x & 3] ? pal[i + 1] : pal[i];
      const o = ((y - y0) * w + x) * 4;
      img.data[o] = c[0];
      img.data[o + 1] = c[1];
      img.data[o + 2] = c[2];
      img.data[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, y0);
}

/** Brightens what is already drawn in rings around a point, dithered. */
export function ditherGlow(ctx: CanvasRenderingContext2D, w: number, h: number, cx: number, cy: number, radius: number, tint: RGB, strength: number) {
  const x0 = Math.max(0, Math.floor(cx - radius));
  const y0 = Math.max(0, Math.floor(cy - radius));
  const x1 = Math.min(w, Math.ceil(cx + radius));
  const y1 = Math.min(h, Math.ceil(cy + radius));
  if (x1 <= x0 || y1 <= y0) return;
  const img = ctx.getImageData(x0, y0, x1 - x0, y1 - y0);
  for (let y = y0; y < y1; y++)
    for (let x = x0; x < x1; x++) {
      const d = Math.hypot(x - cx, y - cy) / radius;
      if (d >= 1) continue;
      const f = (1 - d) ** 2 * strength;
      const steps = Math.floor(f * 4 + BAYER[y & 3][x & 3]);
      if (steps <= 0) continue;
      const o = ((y - y0) * (x1 - x0) + (x - x0)) * 4;
      const k = Math.min(1, steps * 0.13);
      img.data[o] = img.data[o] + (tint[0] - img.data[o]) * k;
      img.data[o + 1] = img.data[o + 1] + (tint[1] - img.data[o + 1]) * k;
      img.data[o + 2] = img.data[o + 2] + (tint[2] - img.data[o + 2]) * k;
    }
  ctx.putImageData(img, x0, y0);
}

export function noise2(seed: number) {
  const r = rng(seed);
  const N = 64;
  const g = Array.from({ length: N * N }, () => r());
  const at = (x: number, y: number) => g[(((y % N) + N) % N) * N + (((x % N) + N) % N)];
  return (x: number, y: number) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;
    const u = fx * fx * (3 - 2 * fx);
    const v = fy * fy * (3 - 2 * fy);
    const a = at(xi, yi) + (at(xi + 1, yi) - at(xi, yi)) * u;
    const b = at(xi, yi + 1) + (at(xi + 1, yi + 1) - at(xi, yi + 1)) * u;
    return a + (b - a) * v;
  };
}

/** A lit pixel moon: shaded as a sphere, banded and dithered, with soft maria. */
export function pixelMoon(r: number, tonesHex?: string[]): HTMLCanvasElement {
  const size = r * 2 + 3;
  const [c, ctx] = layer(size, size);
  const img = ctx.createImageData(size, size);
  const tones = (tonesHex ?? ["#b9b294", "#cfc8a9", "#e1dbbf", "#eee9d3", "#f8f4e4"]).map(hex);
  const maria = noise2(17);
  const L = (() => {
    const v = [-0.55, -0.6, 0.58];
    const m = Math.hypot(v[0], v[1], v[2]);
    return v.map((x) => x / m);
  })();
  const cx = r + 1;
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const dx = (x - cx) / r;
      const dy = (y - cx) / r;
      const d2 = dx * dx + dy * dy;
      if (d2 > 1) continue;
      const nz = Math.sqrt(1 - d2);
      let light = dx * L[0] + dy * L[1] + nz * L[2];
      light = Math.max(0, light) * 0.85 + 0.15;
      // maria: big soft patches, kept faint so they read as terrain, not a face
      const m = maria(x * 0.32 + 3, y * 0.32 + 7) * 0.65 + maria(x * 0.7, y * 0.7) * 0.35;
      if (m > 0.58) light -= 0.13;
      const t = light * (tones.length - 1) + (BAYER[y & 3][x & 3] - 0.5) * 0.9;
      const col = tones[Math.max(0, Math.min(tones.length - 1, Math.round(t)))];
      const o = (y * size + x) * 4;
      img.data[o] = col[0];
      img.data[o + 1] = col[1];
      img.data[o + 2] = col[2];
      img.data[o + 3] = 255;
    }
  ctx.putImageData(img, 0, 0);
  // two small craters near the lit limb, off centre on purpose, in the moon's own tones
  const pit = tonesHex?.[1] ?? "#cbc4a4";
  const rim = tonesHex?.[4] ?? "#f3eedb";
  const crater = (fx: number, fy: number, cr: number) => {
    const px = Math.round(cx + fx * r);
    const py = Math.round(cx + fy * r);
    disc(ctx, px, py, cr, pit);
    ctx.fillStyle = rim;
    ctx.fillRect(px + 1, py + cr, Math.max(1, cr), 1);
  };
  if (r >= 9) {
    crater(-0.45, 0.42, Math.max(1, Math.round(r * 0.09)));
    crater(0.52, -0.18, Math.max(1, Math.round(r * 0.07)));
  }
  return c;
}

export function ridge(w: number, base: number, amp: number, freq: number, seed: number, octaves = 4): number[] {
  const n = fractal(seed, octaves);
  return Array.from({ length: w }, (_, x) => Math.round(base - n(x * freq) * amp));
}

export function fillRidge(ctx: CanvasRenderingContext2D, heights: number[], bottom: number, color: string, top?: string, topEvery = 1) {
  ctx.fillStyle = color;
  heights.forEach((y, x) => ctx.fillRect(x, y, 1, bottom - y));
  if (top) {
    ctx.fillStyle = top;
    heights.forEach((y, x) => {
      if (x % topEvery === 0 && heights[x - 1] !== undefined && heights[x - 1] >= y) ctx.fillRect(x, y, 1, 1);
    });
  }
}

export function disc(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string) {
  ctx.fillStyle = color;
  for (let y = -r; y <= r; y++) {
    const half = Math.round(Math.sqrt(Math.max(0, r * r - y * y)));
    ctx.fillRect(Math.round(cx - half), Math.round(cy + y), half * 2 + 1, 1);
  }
}

export function pixelCloud(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, body: string, light: string, shade: string, r: () => number) {
  const puffs: [number, number, number][] = [];
  const count = 3 + Math.floor(r() * 3);
  for (let i = 0; i < count; i++) puffs.push([x + (i - count / 2) * size * 0.9 + r() * size * 0.4, y - r() * size * 0.5, size * (0.6 + r() * 0.6)]);
  const minX = Math.floor(Math.min(...puffs.map((p) => p[0] - p[2])));
  const maxX = Math.ceil(Math.max(...puffs.map((p) => p[0] + p[2])));
  const bottom = Math.round(y + size * 0.35);
  for (let px = minX; px <= maxX; px++) {
    let top = Infinity;
    for (const [cx, cy, rr] of puffs) {
      const dx = px - cx;
      if (Math.abs(dx) <= rr) top = Math.min(top, Math.round(cy - Math.sqrt(rr * rr - dx * dx)));
    }
    if (top === Infinity || top >= bottom) continue;
    ctx.fillStyle = body;
    ctx.fillRect(px, top, 1, bottom - top);
    ctx.fillStyle = light;
    ctx.fillRect(px, top, 1, 1);
    ctx.fillStyle = shade;
    ctx.fillRect(px, bottom - 1, 1, 1);
  }
}

export function pine(ctx: CanvasRenderingContext2D, x: number, base: number, h: number, dark: string, light?: string) {
  const width = Math.max(3, Math.round(h * 0.42));
  for (let i = 0; i < h; i++) {
    const t = i / h;
    const wv = Math.max(1, Math.round(width * (1 - t) * (0.75 + 0.25 * (i % 3 === 0 ? 1 : 0.7))));
    ctx.fillStyle = dark;
    ctx.fillRect(Math.round(x - wv / 2), base - i, wv, 1);
    if (light && wv > 2 && i % 3 === 1) {
      ctx.fillStyle = light;
      ctx.fillRect(Math.round(x - wv / 2), base - i, 1, 1);
    }
  }
  ctx.fillStyle = dark;
  ctx.fillRect(Math.round(x), base, 1, 2);
}

export function roundTree(ctx: CanvasRenderingContext2D, x: number, base: number, r: number, dark: string, light: string, trunk: string) {
  ctx.fillStyle = trunk;
  ctx.fillRect(Math.round(x), base - Math.round(r * 0.8), 1, Math.round(r * 0.8) + 1);
  disc(ctx, x, base - r * 1.5, r, dark);
  disc(ctx, x - r * 0.25, base - r * 1.75, Math.max(1, Math.round(r * 0.55)), light);
  disc(ctx, x - r * 0.25, base - r * 1.75, Math.max(1, Math.round(r * 0.55)) - 1, dark);
}

export interface Star {
  x: number;
  y: number;
  b: number; // base brightness
  s: number; // twinkle speed
  p: number; // phase
  big: boolean;
}

export function makeStars(w: number, h: number, count: number, maxY: number, seed: number): Star[] {
  const r = rng(seed);
  return Array.from({ length: count }, () => ({
    x: Math.floor(r() * w),
    y: Math.floor(r() * r() * maxY * h),
    b: 0.35 + r() * 0.65,
    s: 0.6 + r() * 2.2,
    p: r() * Math.PI * 2,
    big: r() < 0.06,
  }));
}

export function drawStars(ctx: CanvasRenderingContext2D, stars: Star[], t: number, color: RGB) {
  for (const s of stars) {
    const tw = s.b * (0.62 + 0.38 * Math.sin(t * s.s + s.p));
    const a = Math.round(tw * 4) / 4;
    if (a <= 0) continue;
    ctx.fillStyle = css(color, a);
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big && a > 0.5) {
      ctx.fillStyle = css(color, a * 0.45);
      ctx.fillRect(s.x - 1, s.y, 1, 1);
      ctx.fillRect(s.x + 1, s.y, 1, 1);
      ctx.fillRect(s.x, s.y - 1, 1, 1);
      ctx.fillRect(s.x, s.y + 1, 1, 1);
    }
  }
}

// ---------------------------------------------------------------------------
// Meadow: a moonlit pixel meadow with fireflies

const meadow: Scene = {
  pixel: true,
  fps: 10,
  build(w, h, o) {
    const r = rng(7);
    const [sky, sctx] = layer(w, h);
    ditherSky(sctx, w, 0, h, ["#08102e", "#0c1840", "#122456", "#18306c", "#1f3d7e", "#28508e", "#33639a"]);
    // under words at the top, the moon sits lower in the sky, so a heading never runs over it on a narrow screen
    const moon = { x: Math.round(w * 0.8), y: Math.round(h * (o?.clearTop ? 0.36 : 0.17)), r: Math.max(7, Math.round(h * 0.056)) };
    ditherGlow(sctx, w, h, moon.x, moon.y, moon.r * 4.2, hex("#7d93cf"), 1.1);
    const [land, lctx] = layer(w, h);
    // far range
    const far = ridge(w, h * 0.66, h * 0.13, 0.018, 3);
    fillRidge(lctx, far, h, "#17306a", "#22417f", 2);
    // mid hills
    const mid = ridge(w, h * 0.75, h * 0.07, 0.012, 11, 3);
    fillRidge(lctx, mid, h, "#123852", "#1a4a66");
    // trees on the hills
    for (let x = 4; x < w; x += 3 + Math.floor(r() * 9)) {
      if (r() < 0.35) continue;
      const base = mid[x] + 1;
      if (r() < 0.55) pine(lctx, x, base, 6 + Math.floor(r() * 9), "#0b2638", "#163a50");
      else roundTree(lctx, x, base, 2 + Math.floor(r() * 3), "#0c2a3b", "#1a4560", "#0a2030");
    }
    // the meadow
    const near = ridge(w, h * 0.86, h * 0.035, 0.02, 21, 2);
    fillRidge(lctx, near, h, "#0f3a44");
    ditherSky(lctx, w, Math.round(h * 0.9), h, ["#0f3a44", "#0d3340", "#0b2c38"]);
    for (let x = 0; x < w; x++) {
      const top = near[x];
      lctx.fillStyle = "#16505a";
      if (r() < 0.6) lctx.fillRect(x, top - (r() < 0.3 ? 2 : 1), 1, r() < 0.5 ? 2 : 1);
      for (let y = top + 2; y < h; y += 2 + Math.floor(r() * 4)) {
        if (r() < 0.22) {
          lctx.fillStyle = r() < 0.5 ? "#13454f" : "#0c2f3a";
          lctx.fillRect(x, y, 1, 1 + Math.floor(r() * 2));
        }
      }
    }
    // flowers, thicker near the bottom
    const flowers = ["#9fb4ff", "#e9edff", "#c79be8", "#f3a8c9", "#7fdcae"];
    for (let i = 0; i < w * 0.9; i++) {
      const x = Math.floor(r() * w);
      const depth = r() ** 0.6;
      const y = Math.round(near[x] + 3 + depth * (h - near[x] - 4));
      const c = flowers[Math.floor(r() * flowers.length)];
      lctx.fillStyle = c;
      lctx.fillRect(x, y, 1, 1);
      if (depth > 0.75 && r() < 0.5) {
        lctx.fillRect(x - 1, y, 1, 1);
        lctx.fillRect(x + 1, y, 1, 1);
        lctx.fillRect(x, y - 1, 1, 1);
        lctx.fillStyle = "#f6e6a0";
        lctx.fillRect(x, y, 1, 1);
        lctx.fillStyle = "#0b3a33";
        lctx.fillRect(x, y + 2, 1, 2);
      }
    }
    const clouds = Array.from({ length: 5 }, (_, i) => ({ x: r() * w, y: h * (0.12 + r() * 0.3), size: 3 + r() * 4, speed: 0.25 + r() * 0.5, seed: 40 + i }));
    const cloudArt = clouds.map((c) => {
      const [cv, cctx] = layer(Math.ceil(c.size * 9), Math.ceil(c.size * 3));
      pixelCloud(cctx, cv.width / 2, cv.height * 0.6, c.size, "#203f84", "#2e5299", "#1a346f", rng(c.seed));
      return cv;
    });
    const flies = Array.from({ length: Math.round(w / 28) }, () => ({ x: r() * w, y: h * (0.74 + r() * 0.2), p: r() * 10, s: 0.3 + r() * 0.7 }));
    return { sky, land, moon, moonArt: pixelMoon(moon.r), stars: makeStars(w, h, Math.round((w * h) / 240), 0.62, 5), clouds, cloudArt, flies };
  },
  frame({ ctx, w, t }, s) {
    const st = s as { sky: HTMLCanvasElement; land: HTMLCanvasElement; moon: { x: number; y: number; r: number }; moonArt: HTMLCanvasElement; stars: Star[]; clouds: { x: number; y: number; size: number; speed: number }[]; cloudArt: HTMLCanvasElement[]; flies: { x: number; y: number; p: number; s: number }[] };
    ctx.drawImage(st.sky, 0, 0);
    drawStars(ctx, st.stars, t, [235, 240, 255]);
    // moon
    const m = st.moon;
    ctx.drawImage(st.moonArt, m.x - m.r - 1, m.y - m.r - 1);
    st.clouds.forEach((c, i) => {
      const art = st.cloudArt[i];
      const x = ((c.x + t * c.speed * 2) % (w + art.width)) - art.width;
      ctx.drawImage(art, Math.round(x), Math.round(c.y));
    });
    ctx.drawImage(st.land, 0, 0);
    for (const f of st.flies) {
      const blink = Math.sin(t * 1.7 * f.s + f.p);
      if (blink < 0.2) continue;
      const x = Math.round(f.x + Math.sin(t * 0.35 * f.s + f.p) * 9);
      const y = Math.round(f.y + Math.cos(t * 0.5 * f.s + f.p * 2) * 4);
      ctx.fillStyle = blink > 0.75 ? "rgba(230,255,170,0.95)" : "rgba(200,240,140,0.7)";
      ctx.fillRect(x, y, 1, 1);
      if (blink > 0.85) {
        ctx.fillStyle = "rgba(210,255,150,0.25)";
        ctx.fillRect(x - 1, y, 3, 1);
        ctx.fillRect(x, y - 1, 1, 3);
      }
    }
  },
};

// ---------------------------------------------------------------------------
// Summit: sunrise behind snowy mountains

const summit: Scene = {
  pixel: true,
  fps: 8,
  build(w, h) {
    const r = rng(19);
    const [sky, sctx] = layer(w, h);
    ditherSky(sctx, w, 0, Math.round(h * 0.74), ["#2a1a4e", "#45205f", "#6d2a6c", "#9c3a74", "#cf4f76", "#f0706d", "#f99a6e", "#fdc788"]);
    const sun = { x: Math.round(w * 0.36), y: Math.round(h * 0.6), r: Math.round(h * 0.13) };
    ditherGlow(sctx, w, h, sun.x, sun.y, sun.r * 3.2, hex("#ffd7a0"), 1.2);
    disc(sctx, sun.x, sun.y, sun.r, "#ffe6a8");
    disc(sctx, sun.x, sun.y, sun.r - 2, "#fff0c4");
    // streaky clouds
    const [land, lctx] = layer(w, h);
    for (let i = 0; i < 7; i++) {
      const y = Math.round(h * (0.16 + r() * 0.36));
      const x = r() * w;
      const len = 20 + r() * 60;
      sctx.fillStyle = r() < 0.5 ? "#f7a582" : "#e0727f";
      sctx.fillRect(Math.round(x), y, Math.round(len), 1);
      sctx.fillStyle = "#ffd0a0";
      sctx.fillRect(Math.round(x + len * 0.2), y - 1, Math.round(len * 0.5), 1);
    }
    // far range with snow
    const far = ridge(w, h * 0.62, h * 0.26, 0.012, 9, 5);
    const farMin = Math.min(...far);
    far.forEach((y, x) => {
      lctx.fillStyle = "#8b4a7c";
      lctx.fillRect(x, y, 1, h - y);
      const snowLine = farMin + h * 0.06;
      if (y < snowLine) {
        const lit = x > 0 && far[x - 1] > y; // left-facing slope catches the sun
        lctx.fillStyle = lit ? "#ffd9cf" : "#e6a9b3";
        lctx.fillRect(x, y, 1, Math.max(1, Math.round(snowLine - y)));
      }
    });
    const mid = ridge(w, h * 0.74, h * 0.16, 0.02, 31, 4);
    fillRidge(lctx, mid, h, "#5b2d5e", "#7a3d6c", 1);
    const mist = Math.round(h * 0.8);
    ditherSky(lctx, w, mist, h, ["#4a2552", "#3a1d46", "#2b1636"]);
    const near = ridge(w, h * 0.86, h * 0.06, 0.03, 41, 3);
    fillRidge(lctx, near, h, "#2b1636");
    for (let x = 2; x < w; x += 2 + Math.floor(r() * 4)) {
      if (r() < 0.2) continue;
      pine(lctx, x, near[x] + 1, 7 + Math.floor(r() * 12), "#1d0f2a", "#311a40");
    }
    const birds = Array.from({ length: 4 }, () => ({ x: r() * w, y: h * (0.2 + r() * 0.2), s: 2 + r() * 3, p: r() * 6 }));
    return { sky, land, birds, sun };
  },
  frame({ ctx, w, t }, s) {
    const st = s as { sky: HTMLCanvasElement; land: HTMLCanvasElement; birds: { x: number; y: number; s: number; p: number }[] };
    ctx.drawImage(st.sky, 0, 0);
    ctx.drawImage(st.land, 0, 0);
    ctx.fillStyle = "#3a1f45";
    for (const b of st.birds) {
      const x = Math.round((b.x + t * b.s) % (w + 10)) - 5;
      const y = Math.round(b.y + Math.sin(t * 0.8 + b.p) * 2);
      const flap = Math.sin(t * 6 + b.p) > 0;
      ctx.fillRect(x, y, 1, 1);
      ctx.fillRect(x - 1, y - (flap ? 1 : 0), 1, 1);
      ctx.fillRect(x + 1, y - (flap ? 1 : 0), 1, 1);
      ctx.fillRect(x - 2, y - (flap ? 2 : 0), 1, 1);
      ctx.fillRect(x + 2, y - (flap ? 2 : 0), 1, 1);
    }
  },
};

// ---------------------------------------------------------------------------
// Tide: the sun setting into a pixel sea

const tide: Scene = {
  pixel: true,
  fps: 8,
  build(w, h) {
    const r = rng(23);
    const horizon = Math.round(h * 0.6);
    const [sky, sctx] = layer(w, h);
    ditherSky(sctx, w, 0, horizon, ["#1b1742", "#2c2160", "#4f2a76", "#843a7c", "#c24f7a", "#ea6f74", "#f79870", "#ffc184"]);
    const sun = { x: Math.round(w * 0.5), y: horizon - Math.round(h * 0.07), r: Math.round(h * 0.12) };
    ditherGlow(sctx, w, h, sun.x, sun.y, sun.r * 3, hex("#ffc98a"), 1.2);
    // the sun, sliced by bands of cloud near the horizon
    disc(sctx, sun.x, sun.y, sun.r, "#ffd99a");
    disc(sctx, sun.x, sun.y - 2, sun.r - 3, "#ffe8b4");
    for (let i = 0; i < 4; i++) {
      sctx.fillStyle = "#e8737a";
      const y = sun.y + Math.round(sun.r * (0.25 + i * 0.2));
      sctx.fillRect(sun.x - sun.r - 2, y, sun.r * 2 + 4, 1 + (i > 1 ? 1 : 0));
    }
    sctx.fillStyle = "#1b1742";
    ditherSky(sctx, w, horizon, h, ["#5a3f8c", "#3e3180", "#2b2668", "#201d55", "#171543"]);
    const stars = makeStars(w, h, Math.round(w * 0.5), 0.3, 8);
    // island
    const [land, lctx] = layer(w, h);
    const ix = Math.round(w * 0.82);
    const iw = Math.round(w * 0.14);
    for (let x = -iw; x <= iw; x++) {
      const hh = Math.round(Math.sqrt(Math.max(0, 1 - (x / iw) ** 2)) * h * 0.035) + (x > -iw * 0.2 && x < iw * 0.3 ? Math.round(h * 0.02) : 0);
      lctx.fillStyle = "#1a1336";
      lctx.fillRect(ix + x, horizon - hh, 1, hh + 1);
    }
    // a palm
    const px = ix - Math.round(iw * 0.2);
    const top = horizon - Math.round(h * 0.13);
    for (let y = top; y < horizon - 2; y++) {
      lctx.fillStyle = "#1a1336";
      lctx.fillRect(px + Math.round(((y - top) / (horizon - top)) * 3), y, 1, 1);
    }
    for (const [dx, dy] of [
      [-6, 2], [-4, 0], [-2, -1], [0, -1], [2, -1], [4, 0], [6, 2], [-5, 1], [5, 1], [-7, 3], [7, 3],
    ])
      lctx.fillRect(px + dx, top + dy, 1, 1);
    const sparkles = Array.from({ length: Math.round(w * 0.35) }, () => ({ x: r(), y: r() ** 1.4, p: r() * 6, len: 1 + Math.floor(r() * 4) }));
    return { sky, land, stars, sun, horizon, sparkles };
  },
  frame({ ctx, w, h, t }, s) {
    const st = s as { sky: HTMLCanvasElement; land: HTMLCanvasElement; stars: Star[]; sun: { x: number; r: number }; horizon: number; sparkles: { x: number; y: number; p: number; len: number }[] };
    ctx.drawImage(st.sky, 0, 0);
    drawStars(ctx, st.stars, t, [255, 236, 225]);
    // the sun's road across the water
    const sea = h - st.horizon;
    for (const sp of st.sparkles) {
      const y = st.horizon + 1 + Math.floor(sp.y * (sea - 2));
      const spread = st.sun.r * (0.5 + (sp.y * 2.2));
      const x = Math.round(st.sun.x + (sp.x - 0.5) * 2 * spread + Math.sin(t * 1.3 + sp.p) * 2);
      const on = Math.sin(t * 2 + sp.p * 3) > -0.2;
      if (!on) continue;
      ctx.fillStyle = sp.y < 0.35 ? "#ffe3a8" : sp.y < 0.7 ? "#ffbf86" : "#e98a7c";
      ctx.fillRect(x, y, sp.len + (sp.y > 0.6 ? 2 : 0), 1);
    }
    // little waves
    ctx.fillStyle = "rgba(160, 150, 230, 0.35)";
    for (let i = 0; i < 26; i++) {
      const y = st.horizon + 3 + ((i * 37) % Math.max(4, sea - 4));
      const x = Math.round(((i * 97 + t * (4 + (i % 3))) % (w + 20)) - 10);
      ctx.fillRect(x, y, 3 + (i % 4), 1);
    }
    ctx.drawImage(st.land, 0, 0);
  },
};

// ---------------------------------------------------------------------------
// Daydream: summer afternoon

const daydream: Scene = {
  pixel: true,
  fps: 8,
  build(w, h) {
    const r = rng(31);
    const [sky, sctx] = layer(w, h);
    ditherSky(sctx, w, 0, h, ["#3f97e3", "#56a9ec", "#70baf1", "#8dcaf5", "#acd9f8", "#cbe8fb", "#e3f3fc"]);
    ditherGlow(sctx, w, h, w * 0.14, h * 0.12, h * 0.42, hex("#fffbe0"), 1.1);
    disc(sctx, Math.round(w * 0.14), Math.round(h * 0.12), Math.round(h * 0.055), "#fffbe8");
    const [land, lctx] = layer(w, h);
    const far = ridge(w, h * 0.7, h * 0.12, 0.012, 5);
    fillRidge(lctx, far, h, "#8ec3c9", "#a5d3d6");
    const mid = ridge(w, h * 0.78, h * 0.08, 0.016, 15, 3);
    fillRidge(lctx, mid, h, "#6db86a", "#8bd17f");
    for (let x = 3; x < w; x += 4 + Math.floor(r() * 12)) {
      if (r() < 0.4) continue;
      roundTree(lctx, x, mid[x] + 1, 2 + Math.floor(r() * 3), "#3f8f45", "#64b35c", "#5b4a2f");
    }
    const near = ridge(w, h * 0.88, h * 0.04, 0.02, 25, 2);
    fillRidge(lctx, near, h, "#56a64e");
    ditherSky(lctx, w, Math.round(h * 0.92), h, ["#56a64e", "#4c9a46", "#438e3f"]);
    for (let x = 0; x < w; x++) {
      lctx.fillStyle = "#79c46b";
      if (r() < 0.7) lctx.fillRect(x, near[x] - (r() < 0.4 ? 1 : 0), 1, 1);
      for (let y = near[x] + 2; y < h; y += 2 + Math.floor(r() * 4)) {
        if (r() < 0.2) {
          lctx.fillStyle = r() < 0.5 ? "#62b257" : "#47923f";
          lctx.fillRect(x, y, 1, 1 + Math.floor(r() * 2));
        }
      }
    }
    const flowers = ["#ffffff", "#ffd84d", "#ff7a8a", "#ffb0d0", "#c8a2ff"];
    for (let i = 0; i < w * 0.8; i++) {
      const x = Math.floor(r() * w);
      const depth = r() ** 0.7;
      const y = Math.round(near[x] + 3 + depth * (h - near[x] - 4));
      lctx.fillStyle = flowers[Math.floor(r() * flowers.length)];
      lctx.fillRect(x, y, 1, 1);
      if (depth > 0.7 && r() < 0.5) {
        lctx.fillRect(x - 1, y, 1, 1);
        lctx.fillRect(x + 1, y, 1, 1);
        lctx.fillRect(x, y - 1, 1, 1);
        lctx.fillStyle = "#ffcf3a";
        lctx.fillRect(x, y, 1, 1);
      }
    }
    const clouds = Array.from({ length: 6 }, (_, i) => ({ x: r() * w, y: h * (0.12 + r() * 0.35), size: 4 + r() * 6, speed: 0.4 + r() * 0.6, seed: 60 + i }));
    const cloudArt = clouds.map((c) => {
      const [cv, cctx] = layer(Math.ceil(c.size * 9), Math.ceil(c.size * 3));
      pixelCloud(cctx, cv.width / 2, cv.height * 0.6, c.size, "#ffffff", "#ffffff", "#cfe3f6", rng(c.seed));
      return cv;
    });
    return { sky, land, clouds, cloudArt };
  },
  frame({ ctx, w, t }, s) {
    const st = s as { sky: HTMLCanvasElement; land: HTMLCanvasElement; clouds: { x: number; y: number; speed: number }[]; cloudArt: HTMLCanvasElement[] };
    ctx.drawImage(st.sky, 0, 0);
    st.clouds.forEach((c, i) => {
      const art = st.cloudArt[i];
      const x = ((c.x + t * c.speed * 2) % (w + art.width)) - art.width;
      ctx.drawImage(art, Math.round(x), Math.round(c.y));
    });
    ctx.drawImage(st.land, 0, 0);
  },
};

// ---------------------------------------------------------------------------
// Smooth scenes

export function grain(size = 160, alpha = 18): HTMLCanvasElement {
  const [c, ctx] = layer(size, size);
  const img = ctx.createImageData(size, size);
  const r = rng(99);
  for (let i = 0; i < size * size; i++) {
    const v = Math.floor(r() * 255);
    img.data[i * 4] = v;
    img.data[i * 4 + 1] = v;
    img.data[i * 4 + 2] = v;
    img.data[i * 4 + 3] = alpha;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

const ember: Scene = {
  pixel: false,
  fps: 24,
  build(w, h) {
    const r = rng(41);
    const motes = Array.from({ length: Math.round((w * h) / 26000) + 24 }, () => ({ x: r() * w, y: r() * h, s: 6 + r() * 18, p: r() * 10, size: 0.6 + r() * 1.8, a: 0.25 + r() * 0.6 }));
    return { motes, grain: grain(), r };
  },
  frame({ ctx, w, h, t }, s) {
    const st = s as { motes: { x: number; y: number; s: number; p: number; size: number; a: number }[]; grain: HTMLCanvasElement };
    ctx.fillStyle = "#0b0805";
    ctx.fillRect(0, 0, w, h);
    const g1 = ctx.createRadialGradient(w * 0.5, h * 0.05, 0, w * 0.5, h * 0.05, Math.max(w, h) * 0.75);
    g1.addColorStop(0, "rgba(120, 78, 30, 0.85)");
    g1.addColorStop(0.35, "rgba(70, 44, 18, 0.55)");
    g1.addColorStop(1, "rgba(11, 8, 5, 0)");
    ctx.fillStyle = g1;
    ctx.fillRect(0, 0, w, h);
    const breathe = 0.5 + 0.5 * Math.sin(t * 0.25);
    const g2 = ctx.createRadialGradient(w * 0.82, h * 0.95, 0, w * 0.82, h * 0.95, Math.max(w, h) * 0.55);
    g2.addColorStop(0, `rgba(192, 121, 47, ${0.22 + breathe * 0.08})`);
    g2.addColorStop(1, "rgba(192, 121, 47, 0)");
    ctx.fillStyle = g2;
    ctx.fillRect(0, 0, w, h);
    const g3 = ctx.createRadialGradient(w * 0.08, h * 0.7, 0, w * 0.08, h * 0.7, Math.max(w, h) * 0.45);
    g3.addColorStop(0, "rgba(160, 70, 30, 0.14)");
    g3.addColorStop(1, "rgba(160, 70, 30, 0)");
    ctx.fillStyle = g3;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "lighter";
    for (const m of st.motes) {
      const y = (((m.y - t * m.s) % (h + 40)) + h + 40) % (h + 40) - 20;
      const x = m.x + Math.sin(t * 0.4 + m.p) * 14;
      const flicker = 0.6 + 0.4 * Math.sin(t * 3 + m.p * 7);
      const fade = Math.min(1, y / (h * 0.5));
      ctx.fillStyle = `rgba(255, ${180 + Math.round(m.p * 5) % 60}, 110, ${m.a * flicker * fade})`;
      ctx.beginPath();
      ctx.arc(x, y, m.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = "source-over";
    const pat = ctx.createPattern(st.grain, "repeat");
    if (pat) {
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
    }
    const v = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.8);
    v.addColorStop(0, "rgba(0,0,0,0)");
    v.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, w, h);
  },
};

const aurora: Scene = {
  pixel: false,
  fps: 24,
  build(w, h) {
    const r = rng(53);
    const stars = Array.from({ length: Math.round((w * h) / 2600) }, () => ({ x: r() * w, y: r() * r() * h * 0.8, b: 0.2 + r() * 0.8, s: 0.5 + r() * 2, p: r() * 6, size: r() < 0.08 ? 1.4 : 0.8 }));
    const ridgeN = fractal(77, 5);
    const ridgeY = Array.from({ length: 200 }, (_, i) => h * 0.8 - ridgeN(i * 0.05) * h * 0.18);
    return { stars, ridgeY, grain: grain(160, 10) };
  },
  frame({ ctx, w, h, t }, s) {
    const st = s as { stars: { x: number; y: number; b: number; s: number; p: number; size: number }[]; ridgeY: number[]; grain: HTMLCanvasElement };
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, "#020a10");
    bg.addColorStop(0.55, "#04161e");
    bg.addColorStop(1, "#06222a");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
    for (const sd of st.stars) {
      ctx.fillStyle = `rgba(220, 255, 245, ${sd.b * (0.6 + 0.4 * Math.sin(t * sd.s + sd.p))})`;
      ctx.fillRect(sd.x, sd.y, sd.size, sd.size);
    }
    ctx.globalCompositeOperation = "lighter";
    const bands = [
      { c: [114, 240, 198], y: 0.3, amp: 0.08, f: 1.6, sp: 0.07, hgt: 0.32, a: 0.2 },
      { c: [67, 198, 172], y: 0.36, amp: 0.06, f: 2.3, sp: -0.05, hgt: 0.24, a: 0.16 },
      { c: [138, 184, 255], y: 0.24, amp: 0.07, f: 1.2, sp: 0.04, hgt: 0.2, a: 0.1 },
      { c: [199, 155, 255], y: 0.2, amp: 0.05, f: 2.8, sp: 0.06, hgt: 0.14, a: 0.08 },
    ];
    const step = Math.max(3, Math.round(w / 260));
    for (const b of bands) {
      for (let x = 0; x < w; x += step) {
        const u = x / w;
        const yc = h * (b.y + Math.sin(u * Math.PI * b.f + t * b.sp * 6) * b.amp + Math.sin(u * 9.3 + t * 0.21) * 0.012);
        const hh = h * b.hgt * (0.65 + 0.35 * Math.sin(u * 7.1 + t * 0.33 + b.f));
        const intensity = b.a * (0.6 + 0.4 * Math.sin(u * 13 + t * 0.6));
        const g = ctx.createLinearGradient(0, yc - hh, 0, yc + hh * 0.2);
        g.addColorStop(0, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},0)`);
        g.addColorStop(0.75, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},${intensity})`);
        g.addColorStop(1, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(x, yc - hh, step + 1, hh * 1.2);
      }
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#020a0e";
    ctx.beginPath();
    ctx.moveTo(0, h);
    st.ridgeY.forEach((y, i) => ctx.lineTo((i / (st.ridgeY.length - 1)) * w, y));
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
    const pat = ctx.createPattern(st.grain, "repeat");
    if (pat) {
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
    }
  },
};

const space: Scene = {
  pixel: false,
  fps: 20,
  build(w, h) {
    const r = rng(61);
    const layers = [0.6, 1, 1.6].map((size, i) => Array.from({ length: Math.round((w * h) / (i === 2 ? 26000 : i === 1 ? 6000 : 2200)) }, () => ({ x: r() * w, y: r() * h, b: 0.3 + r() * 0.7, s: 0.4 + r() * 1.8, p: r() * 6, size, hue: r() })));
    const [neb, nctx] = layer(Math.max(1, Math.round(w / 3)), Math.max(1, Math.round(h / 3)));
    const blobs = [
      [0.25, 0.3, 0.55, "rgba(84, 52, 170, 0.5)"],
      [0.6, 0.18, 0.4, "rgba(40, 80, 170, 0.4)"],
      [0.45, 0.55, 0.45, "rgba(150, 50, 140, 0.28)"],
      [0.15, 0.75, 0.35, "rgba(30, 90, 140, 0.25)"],
    ] as const;
    for (const [bx, by, br, c] of blobs) {
      const g = nctx.createRadialGradient(neb.width * bx, neb.height * by, 0, neb.width * bx, neb.height * by, Math.max(neb.width, neb.height) * br);
      g.addColorStop(0, c);
      g.addColorStop(1, "rgba(0,0,0,0)");
      nctx.fillStyle = g;
      nctx.fillRect(0, 0, neb.width, neb.height);
    }
    return { layers, neb, grain: grain(160, 12) };
  },
  frame({ ctx, w, h, t }, s) {
    const st = s as { layers: { x: number; y: number; b: number; s: number; p: number; size: number; hue: number }[][]; neb: HTMLCanvasElement; grain: HTMLCanvasElement };
    ctx.fillStyle = "#04040b";
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 0.9 + 0.1 * Math.sin(t * 0.2);
    ctx.drawImage(st.neb, 0, 0, w, h);
    ctx.globalAlpha = 1;
    st.layers.forEach((stars, li) => {
      for (const sd of stars) {
        const tw = sd.b * (0.55 + 0.45 * Math.sin(t * sd.s + sd.p));
        const x = (sd.x + t * (li + 1) * 0.6) % w;
        const c = sd.hue < 0.15 ? "255, 210, 200" : sd.hue < 0.3 ? "200, 220, 255" : "245, 245, 255";
        ctx.fillStyle = `rgba(${c}, ${tw})`;
        ctx.fillRect(x, sd.y, sd.size, sd.size);
        if (li === 2 && tw > 0.6) {
          ctx.fillStyle = `rgba(${c}, ${tw * 0.18})`;
          ctx.beginPath();
          ctx.arc(x + 0.8, sd.y + 0.8, 3.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });
    // the planet
    const pr = Math.min(w, h) * 0.32;
    const px = w * 0.9;
    const py = h * 1.02;
    const pg = ctx.createRadialGradient(px - pr * 0.4, py - pr * 0.5, pr * 0.1, px, py, pr);
    pg.addColorStop(0, "#5b4aa8");
    pg.addColorStop(0.55, "#2a2160");
    pg.addColorStop(1, "#0b0920");
    ctx.fillStyle = pg;
    ctx.beginPath();
    ctx.arc(px, py, pr, 0, Math.PI * 2);
    ctx.fill();
    const rim = ctx.createRadialGradient(px, py, pr * 0.92, px, py, pr * 1.08);
    rim.addColorStop(0, "rgba(180,162,255,0)");
    rim.addColorStop(0.5, "rgba(180,162,255,0.35)");
    rim.addColorStop(1, "rgba(180,162,255,0)");
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(px, py, pr * 1.08, 0, Math.PI * 2);
    ctx.fill();
    const pat = ctx.createPattern(st.grain, "repeat");
    if (pat) {
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
    }
  },
};

const synth: Scene = {
  pixel: false,
  fps: 30,
  build(w, h) {
    const r = rng(71);
    const stars = Array.from({ length: Math.round((w * h) / 5000) }, () => ({ x: r() * w, y: r() * h * 0.5, b: 0.3 + r() * 0.7, p: r() * 6 }));
    const mN = fractal(13, 3);
    const mountains = Array.from({ length: 60 }, (_, i) => mN(i * 0.18));
    return { stars, mountains };
  },
  frame({ ctx, w, h, t }, s) {
    const st = s as { stars: { x: number; y: number; b: number; p: number }[]; mountains: number[] };
    const horizon = h * 0.6;
    const sky = ctx.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, "#0b0318");
    sky.addColorStop(0.55, "#250a45");
    sky.addColorStop(1, "#6a1b6e");
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, horizon);
    for (const sd of st.stars) {
      ctx.fillStyle = `rgba(255, 220, 255, ${sd.b * (0.6 + 0.4 * Math.sin(t * 1.5 + sd.p))})`;
      ctx.fillRect(sd.x, sd.y, 1.2, 1.2);
    }
    // sun with slices
    const sr = Math.min(w, h) * 0.2;
    const sx = w * 0.5;
    const sy = horizon - sr * 0.35;
    const sg = ctx.createLinearGradient(0, sy - sr, 0, sy + sr);
    sg.addColorStop(0, "#ffd166");
    sg.addColorStop(0.6, "#ff7aa8");
    sg.addColorStop(1, "#ff3fb8");
    ctx.save();
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = sg;
    ctx.fillRect(sx - sr, sy - sr, sr * 2, sr * 2);
    ctx.fillStyle = "#3a0f5a";
    for (let i = 0; i < 7; i++) {
      const y = sy + sr * (0.05 + i * 0.14);
      ctx.fillRect(sx - sr, y, sr * 2, Math.max(1, sr * 0.025 * (i + 1)));
    }
    ctx.restore();
    const glow = ctx.createRadialGradient(sx, sy, sr * 0.6, sx, sy, sr * 2.2);
    glow.addColorStop(0, "rgba(255, 92, 205, 0.25)");
    glow.addColorStop(1, "rgba(255, 92, 205, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, horizon);
    // wireframe mountains
    ctx.fillStyle = "#14062a";
    ctx.strokeStyle = "rgba(77, 231, 255, 0.55)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, horizon);
    st.mountains.forEach((m, i) => ctx.lineTo((i / (st.mountains.length - 1)) * w, horizon - m * h * 0.14 * (Math.abs(i - 30) / 30 + 0.25)));
    ctx.lineTo(w, horizon);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // floor
    const floor = ctx.createLinearGradient(0, horizon, 0, h);
    floor.addColorStop(0, "#1a0630");
    floor.addColorStop(1, "#0a0214");
    ctx.fillStyle = floor;
    ctx.fillRect(0, horizon, w, h - horizon);
    ctx.strokeStyle = "rgba(255, 92, 205, 0.55)";
    ctx.lineWidth = 1.2;
    const vx = w / 2;
    for (let i = -24; i <= 24; i++) {
      ctx.beginPath();
      ctx.moveTo(vx + i * 6, horizon);
      ctx.lineTo(vx + i * w * 0.09, h);
      ctx.stroke();
    }
    const speed = (t * 0.35) % 1;
    for (let i = 0; i < 18; i++) {
      const p = (i + speed) / 18;
      const y = horizon + (h - horizon) * p * p;
      ctx.globalAlpha = Math.min(1, p * 2.2);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const haze = ctx.createLinearGradient(0, horizon - 20, 0, horizon + 40);
    haze.addColorStop(0, "rgba(255, 92, 205, 0)");
    haze.addColorStop(0.5, "rgba(255, 92, 205, 0.25)");
    haze.addColorStop(1, "rgba(255, 92, 205, 0)");
    ctx.fillStyle = haze;
    ctx.fillRect(0, horizon - 20, w, 60);
  },
};

const studio: Scene = {
  pixel: false,
  fps: 0,
  build() {
    return { grain: grain(160, 7) };
  },
  frame({ ctx, w, h }, s) {
    const st = s as { grain: HTMLCanvasElement };
    ctx.fillStyle = "#0c0d10";
    ctx.fillRect(0, 0, w, h);
    const g = ctx.createRadialGradient(w * 0.5, -h * 0.2, 0, w * 0.5, -h * 0.2, Math.max(w, h) * 0.9);
    g.addColorStop(0, "rgba(58, 60, 70, 0.55)");
    g.addColorStop(1, "rgba(12, 13, 16, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    const pat = ctx.createPattern(st.grain, "repeat");
    if (pat) {
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
    }
  },
};

const paper: Scene = {
  pixel: false,
  fps: 0,
  build(w, h) {
    const [c, ctx] = layer(256, 256);
    const r = rng(83);
    const img = ctx.createImageData(256, 256);
    for (let i = 0; i < 256 * 256; i++) {
      const v = 200 + Math.floor(r() * 55);
      img.data[i * 4] = v;
      img.data[i * 4 + 1] = v - 8;
      img.data[i * 4 + 2] = v - 22;
      img.data[i * 4 + 3] = 10;
    }
    ctx.putImageData(img, 0, 0);
    ctx.strokeStyle = "rgba(120, 100, 60, 0.05)";
    for (let i = 0; i < 70; i++) {
      ctx.beginPath();
      const x = r() * 256;
      const y = r() * 256;
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + r() * 20 - 10, y + r() * 20 - 10, x + r() * 30 - 15, y + r() * 30 - 15);
      ctx.stroke();
    }
    void w;
    void h;
    return { fiber: c };
  },
  frame({ ctx, w, h }, s) {
    const st = s as { fiber: HTMLCanvasElement };
    ctx.fillStyle = "#f3efe5";
    ctx.fillRect(0, 0, w, h);
    const g = ctx.createRadialGradient(w * 0.3, h * 0.1, 0, w * 0.3, h * 0.1, Math.max(w, h));
    g.addColorStop(0, "rgba(255, 252, 240, 0.9)");
    g.addColorStop(1, "rgba(232, 224, 205, 0.6)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    const pat = ctx.createPattern(st.fiber, "repeat");
    if (pat) {
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
    }
  },
};

export const SCENES: Record<ThemeId, Scene> = { meadow, summit, tide, daydream, ember, aurora, space, synth, studio, paper };

// A still of each scene, small, for the theme picker. Drawn once and kept.
const thumbs = new Map<string, string>();
export function sceneThumb(id: ThemeId, w = 168, h = 96): string {
  const key = `${id}:${w}x${h}`;
  const hit = thumbs.get(key);
  if (hit) return hit;
  const scene = SCENES[id] ?? SCENES.studio;
  // pixel scenes draw at a quarter size and are scaled up by CSS, like the real thing
  const cw = scene.pixel ? Math.round(w / 2) : w * 2;
  const ch = scene.pixel ? Math.round(h / 2) : h * 2;
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = !scene.pixel;
  try {
    const st = scene.build(cw, ch);
    scene.frame({ ctx, w: cw, h: ch, t: 3 }, st);
  } catch {
    /* a blank thumb beats a broken picker */
  }
  const url = c.toDataURL("image/png");
  thumbs.set(key, url);
  return url;
}
